export const FlagViolationType = {
  Spam: 0,
  Harassment: 1,
  IpInfringement: 2,
  UnsafeContent: 3,
  OffTopic: 4,
  Other: 5
} as const

export type FlagViolationTypeValue = typeof FlagViolationType[keyof typeof FlagViolationType]

export const VIOLATION_TYPE_LABELS: Record<number, string> = {
  [FlagViolationType.Spam]: 'Spam',
  [FlagViolationType.Harassment]: 'Harassment',
  [FlagViolationType.IpInfringement]: 'IP infringement',
  [FlagViolationType.UnsafeContent]: 'Unsafe content',
  [FlagViolationType.OffTopic]: 'Off-topic',
  [FlagViolationType.Other]: 'Other'
}

export const VIOLATION_TYPE_OPTIONS = Object.entries(VIOLATION_TYPE_LABELS).map(([value, label]) => ({
  value: Number(value),
  label
}))

export function getViolationSectionCitation (type: number): string | null {
  switch (type) {
    case FlagViolationType.Spam: return 'Community Guidelines §4'
    case FlagViolationType.Harassment: return 'Community Guidelines §1'
    case FlagViolationType.UnsafeContent: return 'Community Guidelines §3'
    case FlagViolationType.OffTopic: return 'Community Guidelines §2'
    case FlagViolationType.IpInfringement: return 'Terms of Service §§3–4'
    default: return null
  }
}

export function getViolationPolicyPath (type: number): string | null {
  if (type === FlagViolationType.IpInfringement) return '/terms'
  if (type === FlagViolationType.Other) return null
  return '/guidelines'
}

export function formatViolationPublicReason (type: number, subReason?: string): string {
  const label = VIOLATION_TYPE_LABELS[type] ?? 'Other'
  const citation = getViolationSectionCitation(type)
  const core = citation ? `${label} (${citation})` : label
  const extra = subReason?.trim()
  return extra ? `${core}: ${extra}` : core
}

export function getEscalationHint (type: number): string {
  switch (type) {
    case FlagViolationType.Harassment:
      return 'Repeat harassment may warrant suspension. Document pattern before escalating.'
    case FlagViolationType.IpInfringement:
      return 'Escalate for legal review. Preserve evidence for a future DMCA response.'
    case FlagViolationType.UnsafeContent:
      return 'Serious safety issues may need admin review and possible account action.'
    case FlagViolationType.Spam:
      return 'Check for spam rings or bot accounts if reports cluster on one user.'
    default:
      return 'Admin review recommended for edge cases or repeat offenders.'
  }
}

export function suggestViolationFromReason (reason: string): number {
  if (!reason?.trim()) return FlagViolationType.Other
  if (reason.startsWith('[Auto-spam]')) return FlagViolationType.Spam
  const lower = reason.toLowerCase()
  if (lower.includes('spam')) return FlagViolationType.Spam
  if (lower.includes('harass') || lower.includes('hate') || lower.includes('abuse')) return FlagViolationType.Harassment
  if (lower.includes('copyright') || lower.includes('dmca') || lower.includes('infring')) return FlagViolationType.IpInfringement
  if (lower.includes('unsafe') || lower.includes('danger')) return FlagViolationType.UnsafeContent
  if (lower.includes('off topic') || lower.includes('off-topic') || lower.includes('irrelevant')) return FlagViolationType.OffTopic
  return FlagViolationType.Other
}
